/**
 * Per-store title modifier (SEO step 4).
 *
 * One template for every store except the protected ranking stores (owner 2026-10-24): the title, H1 and meta description
 * lead with the store's own main search modifier, taken from `attributes.seo_modifier` (written monthly by SwiftPilot from
 * Search Console): "coupon" | "promo code" | "discount code" | "voucher". Anything else falls back to the site-wide default
 * ("promo code"). A store whose `seo_title_v2` flag is UNSET is on this template too (about 850 visible stores used to render
 * the legacy wording only because the flag was never written); only an explicit `seo_title_v2 === false` (SwiftPilot's reverted
 * rollout: the 100 protected ranking stores) keeps the legacy title / H1 / meta exactly. An unset store without a stored
 * modifier keeps the word it always showed, "coupon", so its link anchors do not change either.
 * Manual `seoTitle` / `h1` / `seoDescription` overrides still win.
 *
 * Pure and unit-tested. Never puts coupon codes in text.
 */
import { parseMoney, parsePercent, reconcileFixedValue, hasDiscountFigure } from "./discount-parse";
import { stripExclusiveWording } from "./exclusive-wording";
import { META_DESCRIPTION_MAX, normalizeMetaText, truncateAtWord } from "./meta-text";
import { buildLegacyDescriptionV2, isLegacyMetaV2Store } from "./legacy-meta-v2";

export const SEO_MODIFIERS = ["coupon", "promo code", "discount code", "voucher"] as const;
export type SeoModifier = (typeof SEO_MODIFIERS)[number];
export const DEFAULT_SEO_MODIFIER: SeoModifier = "promo code";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const PHRASES: Record<SeoModifier, { title: string }> = {
    "coupon": { title: "Coupon Codes" },
    "promo code": { title: "Promo Codes" },
    "discount code": { title: "Discount Codes" },
    "voucher": { title: "Voucher Codes" },
};

const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;

type Attributes = Record<string, unknown> | null | undefined;

export interface SeoCoupon {
    id?: string;
    status?: string | null;
    /** "CODE" | "DEAL": decides whether the title counts "Codes" or "Offers". Absent = "Offers". */
    type?: string | null;
    verified?: boolean | null;
    title: string;
    description?: string | null;
    discountValue?: number | null;
    discountType?: string | null;
    /** Server-only check facts (SEO R3, 2026-10-24); absent = unknown = not trusted. */
    isExclusive?: boolean | null;
    protectedFromExpiry?: boolean | null;
    lastVerdict?: string | null;
    lastVerifiedAt?: Date | string | null;
    createdAt?: Date | string | null;
    expiry?: string | null;
    /**
     * The offer is labelled "Didn't work when we last checked" on the page (store page only, set by lib/store-offers from the
     * check facts: the last verdict EXPIRED / INVALID on a code that is neither merchant-confirmed nor our own). Absent = not failed.
     */
    failedLastCheck?: boolean | null;
    /**
     * Category titles only (2026-10-25): the percentage the coupon's LATEST VALID verification run measured at checkout
     * (lib/category-seo `measuredPercentOfRun`); null/absent = no measurement. Store titles do not read it.
     */
    measuredPercent?: number | null;
}

// --- True labels and titles (SEO R3, owner 2026-10-24: applies to every store page, the protected ones included) -------
// 1. A discount figure in <title> / meta only from a live coupon that is exclusive, merchant-confirmed
//    (protectedFromExpiry) or whose last check was VALID within TRUSTED_CHECK_DAYS. Above 90 % only exclusive or
//    merchant-confirmed (a scraped "100% Off" stays out even when a check said VALID).
// 2. No live offer: "{Brand} Coupons & Promo Codes" (or "Promo Codes & Coupons" on promo stores), no month.
// 3. The month only when the offer list really changed within FRESH_LIST_DAYS (an offer added, expired or checked VALID).

export const TRUSTED_CHECK_DAYS = 30;
export const FRESH_LIST_DAYS = 60;
export const UNCONFIRMED_PERCENT_MAX = 90;
const DAY_MS = 86_400_000;

function toTime(value: Date | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;
    const t = (value instanceof Date ? value : new Date(value)).getTime();
    return Number.isNaN(t) ? null : t;
}

function within(value: Date | string | null | undefined, days: number, now: Date): boolean {
    const t = toTime(value);
    return t !== null && t <= now.getTime() && now.getTime() - t <= days * DAY_MS;
}

/** Exclusive or merchant-confirmed: the only coupons whose figure may exceed 90 % in a title. */
export function isMerchantBacked(c: Pick<SeoCoupon, "isExclusive" | "protectedFromExpiry">): boolean {
    return c.isExclusive === true || c.protectedFromExpiry === true;
}

/** A live coupon whose discount may be stated in a title or meta description (rule 1). */
export function isTitleTrusted(c: SeoCoupon, now: Date = new Date()): boolean {
    if (c.status !== "ACTIVE") return false;
    if (isMerchantBacked(c)) return true;
    return c.lastVerdict === "VALID" && within(c.lastVerifiedAt, TRUSTED_CHECK_DAYS, now);
}

// --- Honest count (audit 2026-10-25 item 1) ----------------------------------------------------------------------------
// The number a title, a meta description, the sidebar sentence and Coupon Insights state = the live offers that did NOT fail their
// last check: deals and codes nobody checked yet count, a code labelled "Didn't work when we last checked" does not. "Verified" =
// an honest offer whose last check was VALID within TRUSTED_CHECK_DAYS, or that the merchant confirmed.

/** A live offer that did not fail its last check. */
export function isHonestOffer(c: Pick<SeoCoupon, "status" | "failedLastCheck">): boolean {
    return c.status === "ACTIVE" && c.failedLastCheck !== true;
}

/** An honest offer verified within the last TRUSTED_CHECK_DAYS days (a VALID check), or confirmed by the merchant. */
export function isVerifiedOffer(c: SeoCoupon, now: Date = new Date()): boolean {
    if (!isHonestOffer(c)) return false;
    if (c.protectedFromExpiry === true) return true;
    return c.lastVerdict === "VALID" && within(c.lastVerifiedAt, TRUSTED_CHECK_DAYS, now);
}

/** The discount a coupon states: stored values first, then its text (thousands-safe parser). */
export function couponDiscount(c: SeoCoupon): { percent: number; fixed: number } {
    const text = `${c.title} ${c.description ?? ""}`;
    if (c.discountType === "PERCENT" && c.discountValue && c.discountValue > 0) return { percent: c.discountValue, fixed: 0 };
    if (c.discountType === "FIXED" && c.discountValue && c.discountValue > 0) return { percent: 0, fixed: reconcileFixedValue(c.discountValue, text) ?? 0 };
    const percent = parsePercent(text);
    if (percent) return { percent, fixed: 0 };
    const money = parseMoney(text);
    return { percent: 0, fixed: money && money.currency === "$" ? Math.round(money.value) : 0 };
}

/** The percentage of a coupon's TITLE (the legacy template reads titles only), within 1..100, or 0. */
function titlePercent(title: string): number {
    return parsePercent(title) ?? 0;
}

/** Percent allowed in a title for this coupon (rule 1: > 90 only when exclusive or merchant-confirmed). */
function allowedPercent(c: SeoCoupon, percent: number): boolean {
    return percent > 0 && percent <= 100 && (percent <= UNCONFIRMED_PERCENT_MAX || isMerchantBacked(c));
}

/**
 * Whether the offer list changed within FRESH_LIST_DAYS: an offer created, an offer checked VALID, or an offer that
 * expired (its own date passed, or the check that expired it) inside the window. Decides the month in the title.
 */
export function offerListChangedRecently(coupons: SeoCoupon[], now: Date = new Date(), days: number = FRESH_LIST_DAYS): boolean {
    return coupons.some((c) => {
        if (c.status !== "ACTIVE" && c.status !== "EXPIRED") return false;
        if (within(c.createdAt, days, now)) return true;
        if (c.lastVerdict === "VALID" && within(c.lastVerifiedAt, days, now)) return true;
        if (c.status === "EXPIRED") {
            if ((c.lastVerdict === "EXPIRED" || c.lastVerdict === "INVALID") && within(c.lastVerifiedAt, days, now)) return true;
            if (c.expiry && /\d{4}|\d{1,2}[/-]\d{1,2}/.test(c.expiry) && within(c.expiry, days, now)) return true;
        }
        return false;
    });
}

export const DESCRIPTION_MIN = 100;

/**
 * Meta description of a non-protected store page, always 100..158 characters (audit 2026-10-24, wave D2). Too short: one or two
 * true sentences about what the page offers are added (nothing about the store's offers is invented). Too long: cut at a word
 * boundary. Applies to the template text and to a writer's custom description alike.
 */
export function fitStoreDescription(description: string, brand: string, subject: string): string {
    let text = normalizeMetaText(description);
    const fillers = [
        `Find ${brand} ${subject} and see how to enter a code at checkout.`,
        "Read each offer's details before you order.",
    ];
    for (const filler of fillers) {
        if (text.length >= DESCRIPTION_MIN) break;
        text = `${text} ${filler}`.trim();
    }
    return truncateAtWord(text, META_DESCRIPTION_MAX);
}

/** The meta description a non-protected store page renders: the writer's custom text when there is one, else the template's; either way 100..158 characters. */
export function renderStoreDescription(custom: string | null | undefined, seo: Pick<StoreSeoV2, "modifier" | "description">, brand: string): string {
    const text = custom && custom.trim() !== "" ? custom : seo.description;
    return fitStoreDescription(text, brand.trim(), subjectOf(seo.modifier === "coupon"));
}

/** Title when the store has no live offer: the store's own keyword order, no month, no discount. */
export function zeroOfferTitle(brand: string, couponStore: boolean): string {
    return couponStore ? `${brand} Coupons & Promo Codes` : `${brand} Promo Codes & Coupons`;
}

/**
 * <title> of a non-protected store with no live offer: the H1 wording, nothing else (audit 2026-10-25 item 4, owner). The "How to Use a
 * Code at Checkout" filler added on 2026-10-24 (wave D, item 17) is gone: it promised a how-to page where the page is a list of
 * expired offers. No count, no discount, no month, because none of them is true for a store without a live offer.
 */
export function zeroOfferPageTitle(brand: string, couponStore: boolean): string {
    return zeroOfferTitle(brand, couponStore);
}

const subjectOf = (couponStore: boolean) => (couponStore ? "coupons and promo codes" : "promo codes and coupons");

function zeroOfferDescription(brand: string, couponStore: boolean): string {
    const subject = subjectOf(couponStore);
    const text = `${brand} ${subject}: how to save on ${brand}, with new offers listed here as they become available.`;
    return text.length > DESCRIPTION_MAX ? text.slice(0, DESCRIPTION_MAX - 3) + "..." : text;
}

/**
 * True for every store except the protected ranking stores: the flag is on when unset or `true`, off only when it is
 * explicitly `false` (owner 2026-10-24: one template for all non-protected stores).
 */
export function isSeoTitleV2(attributes: Attributes): boolean {
    return !(attributes && attributes["seo_title_v2"] === false);
}

/** A protected ranking store: `seo_title_v2` is explicitly false. Its title, H1 and meta description stay on the legacy wording. */
export function isProtectedLegacyStore(attributes: Attributes): boolean {
    return !isSeoTitleV2(attributes);
}

/** Reads `seo_modifier` as a string or as `{ value }`; unknown values -> default. */
export function resolveSeoModifier(attributes: Attributes): SeoModifier {
    const raw = attributes?.["seo_modifier"];
    const value = typeof raw === "string" ? raw : raw && typeof raw === "object" ? (raw as { value?: unknown }).value : undefined;
    if (typeof value !== "string") return DEFAULT_SEO_MODIFIER;
    const normalised = value.trim().toLowerCase().replace(/\s+/g, " ");
    const aliases: Record<string, SeoModifier> = {
        "coupon": "coupon", "coupons": "coupon", "coupon code": "coupon", "coupon codes": "coupon",
        "promo code": "promo code", "promo codes": "promo code", "promo": "promo code",
        "discount code": "discount code", "discount codes": "discount code", "discount": "discount code",
        "voucher": "voucher", "vouchers": "voucher", "voucher code": "voucher", "voucher codes": "voucher",
    };
    return aliases[normalised] ?? DEFAULT_SEO_MODIFIER;
}

/** Whether `seo_modifier` holds a value at all (otherwise the site-wide default applies). */
function hasStoredModifier(attributes: Attributes): boolean {
    const raw = attributes?.["seo_modifier"];
    return typeof raw === "string" ? raw.trim() !== "" : !!raw && typeof raw === "object";
}

/**
 * The word a store's page uses. The stored modifier when there is one; otherwise "promo code" (the site default) for a store
 * that enabled the template explicitly, and "coupon" for the others: a store with the flag unset (or false) has always shown
 * "coupon", and moving it onto the shared template must not change its word or its link anchors.
 */
export function resolveStoreModifier(attributes: Attributes): SeoModifier {
    if (hasStoredModifier(attributes) || attributes?.["seo_title_v2"] === true) return resolveSeoModifier(attributes);
    return "coupon";
}

/**
 * Best discount a title may state (rule 1): only trusted coupons count (isTitleTrusted), percentages win over fixed
 * amounts, and a percentage above 90 needs an exclusive or merchant-confirmed coupon. Returns e.g. "60%" or "$22", or null.
 */
export function bestDiscountLabel(coupons: SeoCoupon[], now: Date = new Date()): string | null {
    let pct = 0;
    let fixed = 0;
    for (const c of coupons) {
        if (!isTitleTrusted(c, now)) continue;
        const d = couponDiscount(c);
        if (allowedPercent(c, d.percent)) pct = Math.max(pct, d.percent);
        else if (d.percent === 0 && d.fixed > 0) fixed = Math.max(fixed, d.fixed);
    }
    if (pct > 0) return `${pct}%`;
    if (fixed > 0) return `$${fixed.toLocaleString("en-US")}`;
    return null;
}

/**
 * The page's "Best Discount" and "Avg. Savings" figures under the same trust rule as the title (rule 1): only trusted
 * live coupons, a percentage above 90 only when exclusive or merchant-confirmed. `best` is "UP TO 60%" (or "$22" when no
 * trusted coupon states a percentage), `average` the mean trusted percentage ("35%"); null = nothing truthful to show.
 */
export function trustedSavingsStats(coupons: SeoCoupon[], now: Date = new Date()): { best: string | null; average: string | null } {
    const percents: number[] = [];
    for (const c of coupons) {
        if (!isTitleTrusted(c, now)) continue;
        const d = couponDiscount(c);
        if (allowedPercent(c, d.percent)) percents.push(d.percent);
    }
    const label = bestDiscountLabel(coupons, now);
    const best = label === null ? null : label.endsWith("%") ? `UP TO ${label}` : label;
    const average = percents.length > 0 ? `${Math.round(percents.reduce((a, b) => a + b, 0) / percents.length)}%` : null;
    return { best, average };
}

/**
 * "a" or "an" for a brand name, keyed on the first letter (a, e, i, o, u take "an"). Deliberately simple: "an OVHcloud",
 * "a Hostinger". Brands whose spoken sound differs from their first letter (rare) are not special-cased.
 */
export function indefiniteArticle(word: string): "a" | "an" {
    return /^[aeiou]/i.test(word.trim()) ? "an" : "a";
}

function fitWithin(candidates: string[], max: number): string {
    return candidates.find((c) => c.length <= max) ?? candidates[candidates.length - 1];
}

/** A fixed, store-independent FAQ item added after the store's own FAQs (v2 stores only). */
export interface TemplateFaq {
    question: string;
    answer: string;
}

/**
 * Template wording of a v2 store page besides title / H1 / meta. Pure text, never a coupon code.
 * Only "coupon" gets the coupon variant; every other modifier (promo code, and the legacy discount code /
 * voucher values the admin selector no longer offers) gets the promo variant.
 */
export interface StorePageCopyV2 {
    /** Active list heading, no date (SEO R3 rule 3, 2026-10-24): "Active {Brand} promo codes". */
    listHeading: string;
    howToHeading: string;
    /** One sentence shown at the start of the How-to-use section, before the steps. */
    howToIntro: string;
    faqHeading: string;
    expiredHeading: string;
    /** Appended to the store's own FAQs only when the store has a FAQ section (no FAQPage JSON-LD on store pages since 2026-10-24). */
    faqItem: TemplateFaq;
}

// `_now` is kept so callers need no change: no heading carries a date any more.
export function buildStorePageCopyV2(brandName: string, modifier: SeoModifier, _now: Date = new Date()): StorePageCopyV2 {
    const brand = brandName.trim();
    const a = indefiniteArticle(brand);
    const A = a === "an" ? "An" : "A";
    if (modifier === "coupon") {
        return {
            listHeading: `Active ${brand} coupon codes`,
            howToHeading: `How to use ${a} ${brand} coupon code`,
            howToIntro: `Copy ${a} ${brand} coupon or promo code, then paste it in the promo field at checkout.`,
            faqHeading: `${brand} Coupon FAQ`,
            expiredHeading: `Recently Expired ${brand} Coupons`,
            faqItem: {
                question: `Does ${brand} accept promo codes as well as coupons?`,
                answer: `Yes. ${A} ${brand} coupon and a promo code are the same thing: paste the code in the promo field at checkout before you pay.`,
            },
        };
    }
    return {
        listHeading: `Active ${brand} promo codes`,
        howToHeading: `How to use ${a} ${brand} promo code`,
        howToIntro: `Copy ${a} ${brand} promo code or coupon, then paste it in the promo field at checkout.`,
        faqHeading: `${brand} Promo Code FAQ`,
        expiredHeading: `Recently Expired ${brand} Promo Codes`,
        faqItem: {
            question: `Is ${a} ${brand} coupon the same as a promo code?`,
            answer: `Yes. ${brand} uses one promo code field at checkout, so a coupon and a promo code work the same way: paste the code there before you pay.`,
        },
    };
}

export interface StoreSeoV2 {
    modifier: SeoModifier;
    title: string;
    h1: string;
    description: string;
    copy: StorePageCopyV2;
}

/**
 * Builds the v2 title / H1 / description and the page copy (H2s, how-to intro, template FAQ item). Returns null for a protected ranking
 * store (`seo_title_v2 === false`): callers then keep the legacy template.
 */
export function buildStoreSeoV2(input: {
    name: string;
    attributes: Attributes;
    coupons: SeoCoupon[];
    /** Unused since the v2 meta no longer carries an "updated" date; kept so callers need no change. */
    lastUpdated?: string | null;
    now?: Date;
    /**
     * Title wave (lib/title-wave): true = the honest-count title and meta (the number counts only offers that did not fail their last
     * check; verified count in the title when there is one). false / absent = today's wording, counted over every live offer.
     */
    honest?: boolean;
}): StoreSeoV2 | null {
    if (!isSeoTitleV2(input.attributes)) return null;

    const now = input.now ?? new Date();
    const month = MONTHS[now.getUTCMonth()];
    const mon = month.slice(0, 3);
    const year = now.getUTCFullYear();
    const modifier = resolveStoreModifier(input.attributes);
    const phrase = PHRASES[modifier];
    const brand = input.name.trim();
    const couponStore = modifier === "coupon";
    // Promo stores: "Promo Codes & Coupons"; coupon stores: "Coupons & Promo Codes". No date in the H1.
    const h1 = zeroOfferTitle(brand, couponStore);
    const copy = buildStorePageCopyV2(brand, modifier, now);
    const activeCount = input.coupons.filter((c) => c.status === "ACTIVE").length;

    // Honest wave (audit 2026-10-25 item 1): the count excludes offers that failed their last check; with none left the title is the
    // plain H1 wording (no number, no month).
    if (input.honest) return buildHonestSeoV2({ ...input, brand, modifier, phrase, couponStore, h1, copy, now, month, mon, year });

    // Rule 2: no live offer -> the H1 wording as title, no month, no discount.
    if (activeCount === 0) {
        return {
            modifier,
            title: zeroOfferPageTitle(brand, couponStore),
            h1,
            description: fitStoreDescription(zeroOfferDescription(brand, couponStore), brand, subjectOf(couponStore)),
            copy,
        };
    }

    const best = bestDiscountLabel(input.coupons, now);
    const fresh = offerListChangedRecently(input.coupons, now);
    // One template for every non-protected store (owner 2026-10-24): the real number of active offers, plus a discount figure
    // only when R3 rule 1 allows it, plus the month only when R3 rule 3 allows it. "Codes" when every live offer is a code,
    // otherwise "Offers" (a deal is not a code). "1 Active Code", never "1 Active Codes".
    const noun = input.coupons.filter((c) => c.status === "ACTIVE").every((c) => c.type === "CODE") ? "Code" : "Offer";
    const core = `${brand} ${phrase.title}: ${activeCount} Active ${noun}${activeCount === 1 ? "" : "s"}`;
    const dated = (text: string) => (fresh ? [`${text} (${mon} ${year})`] : []);
    const title = fitWithin([
        ...(best ? [...dated(`${core}, ${best} Off`), `${core}, ${best} Off`] : []),
        ...dated(core),
        core,
        // A long brand: the figure alone (R3 rule 1) before the bare keyword.
        ...(best ? [`${brand} ${phrase.title}: ${best} Off`] : []),
        `${brand} ${phrase.title}`,
    ], TITLE_MAX);

    // Meta: count and best discount only (no "verified", no "updated <date>", no emoji); the month follows rule 3.
    const facts = [
        `${activeCount} active offer${activeCount === 1 ? "" : "s"}`,
        ...(best ? [`up to ${best} off`] : []),
    ].join(", ");
    const subject = couponStore ? "coupons and promo codes" : "promo codes and coupons";
    const lead = `${brand} ${subject}${fresh ? ` for ${month} ${year}` : ""}: ${facts}.`;
    const tail = "See which code fits your plan and where to enter it at checkout.";
    // The second sentence is always kept; only when brand + facts + tail pass 158 characters is the end cut, at a word boundary.
    const description = fitStoreDescription(`${lead} ${tail}`, brand, subject);

    return { modifier, title, h1, description, copy };

}

/**
 * The title wave's version of the v2 title and meta (audit 2026-10-25 item 1):
 *   {H} honest offers (live, not failed their last check), {V} of them verified (VALID within 30 days, or merchant-confirmed).
 *   V >= 1:  "{Brand} Promo Codes: {V} Verified, {X}% Off (Oct 2026)"
 *   V = 0:   "{Brand} Promo Codes: {H} Active Offers, {X}% Off (Oct 2026)"
 *   H = 0:   "{Brand} Promo Codes & Coupons" (the store's own keyword order, no number, no month)
 * {X}% only from a trusted honest offer (R3 rule 1), the month only when the list changed in 60 days (R3 rule 3).
 */
function buildHonestSeoV2(a: {
    coupons: SeoCoupon[]; brand: string; modifier: SeoModifier; phrase: { title: string }; couponStore: boolean; h1: string;
    copy: StorePageCopyV2; now: Date; month: string; mon: string; year: number;
}): StoreSeoV2 {
    const { brand, modifier, phrase, couponStore, h1, copy, now, month, mon, year } = a;
    const honest = a.coupons.filter(isHonestOffer);
    const H = honest.length;
    const subject = subjectOf(couponStore);
    if (H === 0) {
        return {
            modifier,
            title: zeroOfferTitle(brand, couponStore),
            h1,
            description: fitStoreDescription(zeroOfferDescription(brand, couponStore), brand, subject),
            copy,
        };
    }
    const V = honest.filter((c) => isVerifiedOffer(c, now)).length;
    const best = bestDiscountLabel(honest, now);
    const fresh = offerListChangedRecently(a.coupons, now);
    const noun = honest.every((c) => c.type === "CODE") ? "Code" : "Offer";
    const core = V >= 1
        ? `${brand} ${phrase.title}: ${V} Verified`
        : `${brand} ${phrase.title}: ${H} Active ${noun}${H === 1 ? "" : "s"}`;
    const dated = (text: string) => (fresh ? [`${text} (${mon} ${year})`] : []);
    const title = fitWithin([
        ...(best ? [...dated(`${core}, ${best} Off`), `${core}, ${best} Off`] : []),
        ...dated(core),
        core,
        ...(best ? [`${brand} ${phrase.title}: ${best} Off`] : []),
        `${brand} ${phrase.title}`,
    ], TITLE_MAX);

    const facts = [
        `${H} active offer${H === 1 ? "" : "s"}`,
        ...(V >= 1 ? [`${V} verified`] : []),
        ...(best ? [`up to ${best} off`] : []),
    ].join(", ");
    const lead = `${brand} ${subject}${fresh ? ` for ${month} ${year}` : ""}: ${facts}.`;
    const tail = "See which code fits your plan and where to enter it at checkout.";
    return { modifier, title, h1, description: fitStoreDescription(`${lead} ${tail}`, brand, subject), copy };
}

// --- Legacy template (stores WITHOUT seo_title_v2) --------------------------------------------------------------
// The ranking stores keep this template's wording ("{Name} Coupon Codes (Month Year): X% Off"); since 2026-10-24 (owner:
// "umblă la titluri, mi-o asum") the truth rules above apply to it too: the figure only from a trusted coupon, the month
// only when the list changed in 60 days, and a store with no live offer gets "{Name} Coupons & Promo Codes".

export type LegacyCoupon = SeoCoupon;

/**
 * Title and meta description of a store page on the legacy template (no seo_title_v2).
 * `slug` selects the v2 description (lib/legacy-meta-v2, owner 2026-10-28) for the allowlisted stores only; without a slug, or for any
 * other store, the description is exactly what it always was. The title is the same for every store.
 */
export function buildLegacyStoreMeta(input: { name: string; slug?: string | null; coupons: LegacyCoupon[]; now?: Date }): { title: string; description: string } {
    const date = input.now ?? new Date();
    const month = MONTHS[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const coupons = input.coupons;
    const active = coupons.filter((c) => c.status === "ACTIVE");

    // Rule 2: no live offer (none at all, or expired ones only): no month, no discount, no count, no "Deals & Codes".
    if (active.length === 0) {
        return { title: zeroOfferTitle(input.name, true), description: zeroOfferDescription(input.name, true) };
    }

    // Rule 1: the best percentage of a trusted coupon's title (the legacy template reads titles), > 90 only when backed.
    let maxDiscount = 0;
    let bestCoupon: LegacyCoupon | undefined;
    for (const c of active) {
        if (!isTitleTrusted(c, date)) continue;
        const pct = titlePercent(c.title);
        if (allowedPercent(c, pct) && pct > maxDiscount) {
            maxDiscount = pct;
            bestCoupon = c;
        }
    }
    const titleSuffix = maxDiscount > 0 ? `: ${maxDiscount}% Off` : ": Deals & Codes";
    // Rule 3: the month only when the offer list changed in the last 60 days.
    const period = offerListChangedRecently(coupons, date) ? ` (${month} ${year})` : "";

    // "Latest Deal": the trusted best coupon; without one, the first live offer whose title states no figure (an untrusted
    // "25% Off" must not reach the meta through it either). Its "exclusive" wording follows rule 4.
    const dealSource = bestCoupon ?? active.find((c) => !hasDiscountFigure(c.title));
    const bestDealTitle = dealSource ? (dealSource.isExclusive ? dealSource.title : stripExclusiveWording(dealSource.title)) : null;

    const title = `${input.name} Coupon Codes${period}${titleSuffix}`;
    const activeCount = active.length;
    const verifiedPrefix = active.some((c) => c.verified === true) ? "\u2705 Verified " : "";

    if (isLegacyMetaV2Store(input.slug)) {
        // v2 (description only): the same candidates as above in the same order (the trusted best coupon, then the live offers whose
        // title states no figure), each in its public wording; generic titles are skipped inside buildLegacyDescriptionV2.
        const publicTitle = (c: LegacyCoupon) => (c.isExclusive ? c.title : stripExclusiveWording(c.title));
        const candidates = [...(bestCoupon ? [bestCoupon] : []), ...active.filter((c) => c !== bestCoupon && !hasDiscountFigure(c.title))];
        return {
            title,
            description: buildLegacyDescriptionV2({
                name: input.name,
                period,
                verifiedPrefix,
                maxDiscount,
                activeCount,
                dealTitles: candidates.map(publicTitle),
                dealsOnly: active.every((c) => c.type === "DEAL"),
            }),
        };
    }

    const discountClaim = maxDiscount > 0
        ? `Up to ${maxDiscount}% with ${activeCount} active promo codes`
        : `${activeCount} active promo codes & deals`;
    let description = `${verifiedPrefix}${input.name} Coupon Codes${period}: ${discountClaim}.${bestDealTitle ? ` \u{1F7E2} Latest Deal: [${bestDealTitle}]` : ""}`;
    if (description.length > 160) description = description.slice(0, 157) + "...";
    return { title, description };
}

/** The legacy H1 / H2 wording of a store page (stores without seo_title_v2). */
// `_period` is kept so callers need no change: the list heading has no date since 2026-10-24 (SEO R3 rule 3).
export function legacyStorePageCopy(name: string, _period?: string) {
    return {
        h1: `${name} Coupon & Promo Codes`,
        listHeading: `Active ${name} coupon codes`,
        expiredHeading: "Expired Offers (May still work)",
        aboutHeading: `About ${name} & Latest Deals`,
        howToHeading: `How to use ${name} Discount Codes?`,
        faqHeading: `${name} Discount FAQs`,
    };
}

// --- Admin selector (owner decision 2026-10-11) -----------------------------------------------------------------
// Per store the owner picks Auto / Coupon / Promo Code. Coupon and Promo Code lock the modifier (SwiftPilot's monthly
// decision never overrides it, the API answers 409 seo_modifier_locked); Auto unlocks it and keeps the current word
// until SwiftPilot's next decision. The lock lives in `attributes.seo_modifier_locked` (owner-written, never by the API).

export const SEO_KEYWORD_CHOICES = ["auto", "coupon", "promo code"] as const;
export type SeoKeywordChoice = (typeof SEO_KEYWORD_CHOICES)[number];

export function isSeoKeywordChoice(value: unknown): value is SeoKeywordChoice {
    return typeof value === "string" && (SEO_KEYWORD_CHOICES as readonly string[]).includes(value);
}

/** True only when the owner picked the modifier by hand. */
export function isSeoModifierLocked(attributes: Attributes): boolean {
    return !!attributes && attributes["seo_modifier_locked"] === true;
}

export interface SeoKeywordState {
    /** Mode the admin shows: "auto" unless the owner locked the modifier. */
    choice: SeoKeywordChoice;
    /** The modifier the store page uses when the template is enabled. */
    modifier: SeoModifier;
    locked: boolean;
    /** Whether the per-store template (`seo_title_v2`) is on. */
    enabled: boolean;
    /** Who set the modifier: the owner (locked), SwiftPilot (a stored value) or nobody (site default). */
    source: "owner" | "swiftpilot" | "default";
}

export function describeSeoKeyword(attributes: Attributes): SeoKeywordState {
    const modifier = resolveStoreModifier(attributes);
    const locked = isSeoModifierLocked(attributes);
    const choice: SeoKeywordChoice = locked && (modifier === "coupon" || modifier === "promo code") ? modifier : "auto";
    return {
        choice,
        modifier,
        locked: choice !== "auto",
        enabled: isSeoTitleV2(attributes),
        source: choice !== "auto" ? "owner" : hasStoredModifier(attributes) ? "swiftpilot" : "default",
    };
}

/**
 * Attributes after the owner picks `choice`. Coupon / Promo Code: modifier + lock + template on. Auto: only the lock
 * is lifted (the current modifier stays until SwiftPilot decides again). Everything else is kept; the input is not mutated.
 */
export function applySeoKeywordChoice(attributes: Attributes, choice: SeoKeywordChoice): Record<string, unknown> {
    const base: Record<string, unknown> = attributes && typeof attributes === "object" && !Array.isArray(attributes) ? { ...attributes } : {};
    if (choice === "auto") return { ...base, seo_modifier_locked: false };
    return { ...base, seo_modifier: choice, seo_modifier_locked: true, seo_title_v2: true };
}

/** The keys the selector and SwiftPilot own: a Brand Attributes ("Soul") save must not overwrite them with stale values. */
export const SEO_MANAGED_ATTRIBUTE_KEYS = ["seo_title_v2", "seo_modifier", "seo_modifier_locked"] as const;

/** Submitted attributes with the SEO-managed keys taken from what is stored now (absent there = absent here). */
export function keepSeoManagedKeys(submitted: Record<string, unknown>, stored: Attributes): Record<string, unknown> {
    const next = { ...submitted };
    for (const key of SEO_MANAGED_ATTRIBUTE_KEYS) {
        if (stored && key in stored) next[key] = stored[key];
        else delete next[key];
    }
    return next;
}

// --- Visible anchor text of internal links to store pages (owner decision 2026-10-11) -------------------------------
// Only two keyword words exist: "coupon" and "promo code". A link to a store page reads "{Name} coupons" or
// "{Name} promo codes" (plural on cards and related lists, singular in A-Z / "More ..." lists), following the word
// the store's own page uses. Stores without the v2 template keep the legacy wording: coupon. The text is VISIBLE
// inside the <a>; there is no aria-label (see components/StoreTile). Pure and unit-tested.

export type AnchorPlacement = "card" | "related" | "list";
export type AnchorModifier = "coupon" | "promo code";

const ANCHOR_WORDS: Record<AnchorModifier, { plural: string; singular: string }> = {
    "coupon": { plural: "coupons", singular: "coupon" },
    "promo code": { plural: "promo codes", singular: "promo code" },
};

/** The word a store's page really renders: the stored modifier when `seo_title_v2` is on (anything but "promo code" counts as coupon), otherwise "coupon" (legacy template). */
export function effectiveModifier(attributes: Attributes): AnchorModifier {
    return isSeoTitleV2(attributes) && resolveStoreModifier(attributes) === "promo code" ? "promo code" : "coupon";
}

/** Compact form for payloads sent to the browser: 1 = "promo code", 0 = "coupon". */
export function anchorFlag(attributes: Attributes): 0 | 1 {
    return effectiveModifier(attributes) === "promo code" ? 1 : 0;
}

/** Keyword part for a flag from `anchorFlag`, e.g. "promo codes". Plural except in "list" placements. */
export function anchorSuffixForFlag(flag: 0 | 1 | boolean | null | undefined, placement: AnchorPlacement): string {
    const words = ANCHOR_WORDS[flag ? "promo code" : "coupon"];
    return placement === "list" ? words.singular : words.plural;
}

/** Keyword part only, lowercase: "promo codes" / "coupons" (card, related) or "promo code" / "coupon" (list). */
export function storeAnchorSuffix(attributes: Attributes, placement: AnchorPlacement): string {
    // A REFERRAL store (mirror key `page_type`, lib/page-type) is linked as "{Name} referral code" on every page (owner 2026-11-04). No other store has the key.
    if (attributes && (attributes as Record<string, unknown>).page_type === "REFERRAL") return "referral code";
    return anchorSuffixForFlag(anchorFlag(attributes), placement);
}

/** Full anchor text, e.g. "DMIT promo codes" / "Brevo coupons". */
export function storeAnchorText(store: { name: string; attributes: Attributes }, placement: AnchorPlacement): string {
    return `${store.name.trim()} ${storeAnchorSuffix(store.attributes, placement)}`;
}
